import { useState } from 'react'
import type { ArtefactPorteDTO, Classe, Emplacement, ObjetDTO, Stat } from '@whatremains/shared'
import { emplacementRetenu, PRISE } from '@whatremains/shared'
import { NomObjet } from './NomObjet'
import { EmblemeClasse } from './Glyphes'
import { empiler } from '@whatremains/shared'
import { IconeObjet } from './IconeObjet'
import { typeDit } from './TypeObjet'
import { Ecart } from './Comparaison'
import { useAuDoigt } from './pointeur'

/**
 * La poupée d'équipement, et les sacs sous elle.
 *
 * Douze emplacements autour d'un emblème de classe, plus le treizième qui
 * n'appartient qu'à l'artefact. Elle remplace une liste de lignes de texte :
 * `08-INTERFACE.md` demande qu'on voie ce qu'on porte sans le lire.
 *
 * **Les deux gestes sont au même endroit.** Retirer une pièce se fait en
 * cliquant dessus ; en porter une se fait depuis les sacs, juste dessous, sans
 * changer d'onglet. Et cliquer un emplacement filtre les sacs sur ce qui s'y
 * loge, ce qui évite de chercher une paire de bottes au milieu de trente
 * pièces. Le filtre se relâche en recliquant le même emplacement.
 *
 * Le détail ne flotte pas au-dessus du curseur, il s'écrit dans un panneau fixe
 * entre la poupée et les sacs. Deux raisons : un panneau fixe ne recouvre
 * jamais ce qu'on est en train de comparer, et il ne bouge pas pendant qu'on
 * descend la colonne.
 */

const NOMS_EMPLACEMENTS: Record<Emplacement, string> = {
  tete: 'Tête',
  cou: 'Cou',
  torse: 'Torse',
  dos: 'Dos',
  mains: 'Mains',
  taille: 'Taille',
  jambes: 'Jambes',
  pieds: 'Pieds',
  arme_principale: 'Arme',
  arme_secondaire: 'Main gauche',
  anneau_1: 'Anneau',
  anneau_2: 'Anneau',
}

const NOMS_STATS: Record<Stat, string> = {
  force: 'Force',
  adresse: 'Adresse',
  esprit: 'Esprit',
  vigueur: 'Vigueur',
  fortune: 'Fortune',
}

/** Gauche, droite, puis les armes en bas. Le corps se lit de haut en bas. */
const GAUCHE: Emplacement[] = ['tete', 'cou', 'torse', 'dos', 'mains']
const DROITE: Emplacement[] = ['taille', 'jambes', 'pieds', 'anneau_1', 'anneau_2']
const ARMES: Emplacement[] = ['arme_principale', 'arme_secondaire']

/** Ce que le survol a saisi : une pièce, et si elle est portée ou dans les sacs. */
type Vise = { objet: ObjetDTO; portee: boolean }

function Case({
  emplacement,
  objet,
  filtre,
  vise,
  auDoigt,
  barree,
  destination,
  surSurvol,
  surRetirer,
  surFiltrer,
}: {
  emplacement: Emplacement
  objet: ObjetDTO | undefined
  filtre: Emplacement | null
  vise: Vise | null
  auDoigt: boolean
  /** Barrée : rien ne peut y aller tant que l'autre main tient à deux mains. */
  barree?: boolean
  /**
   * Là où la pièce visée dans les sacs atterrirait si on la prenait.
   *
   * `'retenue'` est l'emplacement que le serveur choisira, `'possible'` un
   * autre où elle POURRAIT aller — le second doigt, la seconde main. La
   * distinction n'existe que pour les pièces à deux destinations, et c'est
   * précisément là qu'Enzo n'était pas sûr.
   */
  destination?: 'retenue' | 'possible'
  surSurvol: (v: Vise | null) => void
  surRetirer: (o: ObjetDTO) => void
  surFiltrer: (e: Emplacement) => void
}) {
  // Le palier n'est plus peint ici : il est dans le cadre de l'icône, qui le dit
  // par sa forme autant que par sa couleur. La case redevient une case.
  const classes = [
    'case-equipement',
    objet ? 'pleine' : 'vide',
    barree ? 'barree' : '',
    !objet && filtre === emplacement ? 'visee' : '',
    objet && vise?.objet.id === objet.id ? 'visee' : '',
    destination === 'retenue' ? 'destination' : '',
    destination === 'possible' ? 'destination-autre' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      type="button"
      className={classes}
      onMouseEnter={() => !auDoigt && objet && surSurvol({ objet, portee: true })}
      onFocus={() => objet && surSurvol({ objet, portee: true })}
      onMouseLeave={() => !auDoigt && surSurvol(null)}
      onBlur={() => !auDoigt && surSurvol(null)}
      disabled={barree}
      onClick={() => {
        if (barree) return
        if (!objet) {
          surFiltrer(emplacement)
          return
        }
        // Au doigt, le premier appui vise et le second seulement agit : sans ça
        // on retirerait une pièce sans avoir jamais pu lire ce qu'elle valait.
        if (auDoigt && vise?.objet.id !== objet.id) {
          surSurvol({ objet, portee: true })
          return
        }
        // Le panneau décrit une pièce portée. Une fois retirée, il mentirait
        // en proposant encore de la retirer.
        surSurvol(null)
        surRetirer(objet)
      }}
      aria-label={
        destination
          ? `${NOMS_EMPLACEMENTS[emplacement]} : ${
              destination === 'retenue'
                ? 'c’est là que la pièce visée irait.'
                : 'la pièce visée pourrait aussi aller là.'
            }`
          : barree
          ? `${NOMS_EMPLACEMENTS[emplacement]} : tes deux mains sont prises.`
          : objet
            ? `${objet.nom}, porté à ${NOMS_EMPLACEMENTS[emplacement]}. Retirer.`
            : `${NOMS_EMPLACEMENTS[emplacement]}, vide. Voir ce qui s’y loge.`
      }
      aria-pressed={objet ? undefined : filtre === emplacement}
    >
      {objet ? (
        <IconeObjet objet={objet} taille={46} />
      ) : (
        <span className="case-nom">{NOMS_EMPLACEMENTS[emplacement]}</span>
      )}
      {barree && <span className="case-barre" aria-hidden="true" />}
    </button>
  )
}

function CaseSac({
  objet,
  quantite,
  portable,
  vise,
  auDoigt,
  surSurvol,
  surEquiper,
  surConsommer,
}: {
  objet: ObjetDTO
  /** Combien d'identiques, cran d'amélioration compris. */
  quantite: number
  portable: boolean
  vise: Vise | null
  auDoigt: boolean
  surSurvol: (v: Vise | null) => void
  surEquiper: (o: ObjetDTO) => void
  surConsommer: (o: ObjetDTO) => void
}) {
  // Un consommable ne se PORTE pas, il se prend. La tuile proposait « porter »
  // pour tout, donc l'Eau de suif se présentait comme un équipement.
  const seBoit = objet.categorie === 'consommable'
  return (
    <button
      type="button"
      className={
        `case-equipement case-sac${portable ? ' pleine' : ' hors-portee'}` +
        `${vise?.objet.id === objet.id ? ' visee' : ''}`
      }
      onMouseEnter={() => !auDoigt && surSurvol({ objet, portee: false })}
      onFocus={() => surSurvol({ objet, portee: false })}
      onMouseLeave={() => !auDoigt && surSurvol(null)}
      onBlur={() => !auDoigt && surSurvol(null)}
      onClick={() => {
        // Au doigt, on vise d'abord. Vaut aussi pour une pièce hors de portée :
        // l'appui sert alors à lire pourquoi elle l'est.
        if (auDoigt && vise?.objet.id !== objet.id) {
          surSurvol({ objet, portee: false })
          return
        }
        if (seBoit) {
          surSurvol(null)
          surConsommer(objet)
          return
        }
        if (!portable) return
        surSurvol(null)
        surEquiper(objet)
      }}
      // Pas `disabled` : un bouton désactivé n'émet aucun évènement de souris,
      // donc le panneau resterait muet sur la seule pièce qu'on a justement
      // besoin de lire, celle qu'on ne peut pas encore porter. Le clic est
      // refusé plus haut, et `aria-disabled` le dit aux lecteurs d'écran.
      aria-disabled={!portable && !seBoit}
      aria-label={
        seBoit
          ? `${objet.nom}${quantite > 1 ? `, ${quantite} en réserve` : ''}. Prendre.`
          : portable
            ? `${objet.nom}, dans les sacs. Porter.`
            : `${objet.nom}, dans les sacs. Il faut le niveau ${objet.niveauRequis}.`
      }
    >
      <IconeObjet objet={objet} taille={38} />
      {/* Le compte de la pile, sur la tuile. Cinq ferrailles occupaient cinq
          cases, ce que rien ne justifie : elles sont interchangeables. */}
      {quantite > 1 && <span className="case-compte">{quantite}</span>}
    </button>
  )
}

export function Poupee({
  classe,
  niveau,
  portes,
  ranges,
  artefact,
  surPoser,
  surRetirer,
  surEquiper,
  surConsommer,
}: {
  classe: Classe
  niveau: number
  portes: Map<Emplacement, ObjetDTO>
  ranges: ObjetDTO[]
  artefact: ArtefactPorteDTO | null
  /** Poser l'ultime. Absent quand l'écran ne sait pas le faire. */
  surPoser?: () => void
  surRetirer: (o: ObjetDTO) => void
  surEquiper: (o: ObjetDTO, emplacement?: Emplacement) => void
  /** Boire, manger, appliquer. Un consommable ne se porte pas. */
  surConsommer: (o: ObjetDTO) => void
}) {
  const [vise, setVise] = useState<Vise | null>(null)
  const [filtre, setFiltre] = useState<Emplacement | null>(null)
  const auDoigt = useAuDoigt()

  const basculerFiltre = (e: Emplacement) => setFiltre((f) => (f === e ? null : e))

  /*
   * Le filtre suit la règle des mains, pas le seul emplacement du modèle.
   *
   * Une dague se loge dans les deux mains : cliquer la main gauche doit donc
   * la proposer, alors que son emplacement de modèle est l'arme principale.
   * Sans ça, la moitié de la règle serait vraie côté serveur et invisible ici.
   */
  const sacs = filtre
    ? ranges.filter((o) => (o.emplacementsPossibles ?? [o.emplacementCible]).includes(filtre))
    : ranges

  /*
   * Où irait la pièce qu'on vise dans les sacs.
   *
   * Enzo : « quand je clique sur un accessoire de mon inventaire, j'aimerais
   * que ça encadre la case de la poupée vers là où ça va aller, juste pour que
   * je puisse voir. Parce que parfois on n'est pas toujours sûr. »
   *
   * Il ne l'était pas à raison : la poupée ne montrait la destination NULLE
   * PART. Elle savait dire quelle case tenait la pièce survolée, et quelles
   * pièces vont dans une case cliquée — jamais l'inverse, qui est pourtant le
   * sens dans lequel on équipe.
   *
   * Une seule pièce peut viser DEUX cases (les deux doigts, les deux mains
   * d'un rogue) : on marque alors la retenue et l'autre différemment, sans
   * quoi on aurait remplacé « je ne sais pas où ça va » par « je ne sais pas
   * laquelle des deux ». Le choix vient de `emplacementRetenu`, la même
   * fonction que la route appelle — la montrer sans la partager aurait voulu
   * dire l'écrire deux fois.
   */
  const principale = portes.get('arme_principale')
  const deuxMainsPrises = !!principale && PRISE[principale.famille] === 'deux_mains'
  const destinations =
    vise && !vise.portee
      ? (vise.objet.emplacementsPossibles ?? [vise.objet.emplacementCible])
      : []
  const retenue = emplacementRetenu(destinations, (e) => portes.has(e), deuxMainsPrises)
  const destinationDe = (e: Emplacement): 'retenue' | 'possible' | undefined =>
    !destinations.includes(e) ? undefined : e === retenue ? 'retenue' : 'possible'

  const colonne = (emplacements: Emplacement[]) => (
    <div className="colonne-cases">
      {emplacements.map((e) => (
        <Case
          key={e}
          emplacement={e}
          objet={portes.get(e)}
          filtre={filtre}
          vise={vise}
          auDoigt={auDoigt}
          destination={destinationDe(e)}
          surSurvol={setVise}
          surRetirer={surRetirer}
          surFiltrer={basculerFiltre}
        />
      ))}
    </div>
  )

  // Ce que la pièce visée remplacerait, pour que la comparaison ait un terme.
  const remplacee = vise && !vise.portee ? portes.get(vise.objet.emplacementCible) : undefined
  const portable = vise ? vise.objet.niveauRequis <= niveau : false

  return (
    <div className="poupee">
      <div className={`poupee-corps${artefact ? ' avec-artefact' : ''}`}>
        {colonne(GAUCHE)}

        <div className="poupee-centre">
          <div className="embleme" aria-hidden="true">
            <EmblemeClasse classe={classe} taille={86} />
          </div>

          {/*
            Le treizième emplacement. Il n'existe que pour l'artefact, il ne
            reçoit rien d'autre, et il reste visible vide : c'est une place
            qu'on voit avant de savoir ce qui la remplit.
          */}
          {/*
            Ce qui se lit ici est le SEUL canal par lequel le porteur apprend où
            il en est. Le canon l'écrit : la modification de l'apparence par la
            jauge est la seule altération visuelle possible de ces objets, et
            rien d'autre au monde ne peut les marquer.

            D'où la forme : l'aspect en toutes lettres, et pas un libellé.
            « Il commence à sauter des battements » se lit et se retient ;
            « état IV » se lit comme une barre de progression, c'est-à-dire
            comme la mesure qu'on a précisément décidé de ne pas donner. Le
            chiffre reste quand même, parce que le porteur a le droit de savoir
            dans quelle bande il est. Jamais où il en est dedans.
          */}
          <div className={`case-artefact${artefact ? ' portee' : ''}`}>
            {artefact ? (
              <>
                {/*
                  Le portrait de l'objet.

                  C'est le seul endroit du jeu où l'on regarde un artefact, et
                  jusqu'ici on n'y lisait que son nom. L'image est peinte, pas
                  tracée : les huit ne partagent rien avec le reste du
                  catalogue, ils n'ont ni palier ni matériau, et ils ne se
                  rangent dans aucune famille.
                */}
                <img
                  className="artefact-portrait"
                  src={`/icones/artefacts/${artefact.code}.png`}
                  alt=""
                  aria-hidden="true"
                  onError={(e) => { e.currentTarget.style.display = 'none' }}
                />
                <b>{artefact.nom}</b>
                <span className="artefact-etat">{artefact.etat}</span>
                <p className="artefact-aspect">{artefact.aspect}</p>
                {artefact.ligne && <p className="artefact-ligne">{artefact.ligne}</p>}
                {/*
                  Comment on le pose, et le geste lui-même s'il est possible.

                  La condition est écrite en clair — elle appartient au lore de
                  l'objet, pas à sa jauge — mais le bouton n'apparaît qu'au
                  moment où le geste existe, et jamais avec l'explication de ce
                  qui manque. Perennis ne se pose qu'en pleine course : la
                  fenêtre est étroite par construction, et c'est le propos.
                */}
                {artefact.sortie && <p className="artefact-sortie">{artefact.sortie}</p>}
                {artefact.posable && surPoser && (
                  <button className="action" onClick={() => surPoser()}>
                    Le poser
                  </button>
                )}
              </>
            ) : (
              <span className="tenu">·</span>
            )}
          </div>
        </div>

        {colonne(DROITE)}
      </div>

      {/*
        La seconde main se barre quand la première tient une arme à deux mains.
        Règle d'Enzo : « si le rogue utilise une épée, il ne peut pas avoir de
        seconde main, la case doit passer barrée ». La case reste visible, elle
        n'est pas retirée : on doit voir qu'on a une main en moins, pas croire
        que le jeu n'en a jamais eu qu'une.
      */}
      <div className="poupee-armes">
        {ARMES.map((e) => {
          const principale = portes.get('arme_principale')
          const barree =
            e === 'arme_secondaire' &&
            !portes.get('arme_secondaire') &&
            principale !== undefined &&
            PRISE[principale.famille] === 'deux_mains'
          return (
            <Case
              key={e}
              emplacement={e}
              objet={portes.get(e)}
              filtre={filtre}
              vise={vise}
              auDoigt={auDoigt}
              barree={barree}
              surSurvol={setVise}
              surRetirer={surRetirer}
              surFiltrer={basculerFiltre}
            />
          )
        })}
      </div>

      {/*
        La place est RÉSERVÉE ici, le contenu est peint par-dessus.

        Enzo, sur PC : « sur des descriptions longues on a un ascenseur, sauf
        que si on retire la souris de l'item on n'a plus la description, donc
        l'ascenseur est inutilisable. » Les deux moitiés du défaut se tenaient :
        le panneau avait une hauteur FIXE avec défilement — à raison, il est
        au-dessus de la grille des sacs, et s'il grandissait il poussait les
        cases sous le curseur — mais on ne peut pas atteindre une barre de
        défilement qu'on efface en s'en approchant.

        Mesuré : le pire contenu du catalogue fait 150 px pour une boîte de
        104. La boîte garde donc ses 104 px pour que rien ne bouge en dessous,
        et le corps du panneau est peint en absolu, à sa vraie hauteur, par
        dessus ce qui suit. Plus de barre à atteindre, puisqu'il n'y a plus
        rien à faire défiler. `pointer-events: none` garantit qu'il ne vole
        jamais le survol de la case qu'il recouvre — sans quoi on fabriquerait
        une boucle : le panneau masque la case, la case perd le survol, le
        panneau disparaît.
      */}
      <div className="detail-objet">
        <div className="detail-corps" aria-live="polite">
        {vise ? (
          <>
            <NomObjet o={vise.objet} niveau={niveau} />
            <span>
              niveau {vise.objet.niveauRequis} · {vise.objet.valeurMarchande} or
              {/* `emplacementCible` et non `emplacement` : le second dit où la
                  pièce EST posée, nul quand elle est au sac. */}
              {typeDit({ ...vise.objet, emplacement: vise.objet.emplacementCible })
                && ` · ${typeDit({ ...vise.objet, emplacement: vise.objet.emplacementCible })}`}
              {Object.entries(vise.objet.bonus).length > 0 &&
                ' · ' +
                  Object.entries(vise.objet.bonus)
                    .map(([s, v]) => `${NOMS_STATS[s as Stat]} +${v}`)
                    .join(', ')}
            </span>
            {/*
              Ce qu'un consommable FAIT, et ce qu'il raconte.

              Enzo : « pour l'Eau de suif et l'Onguent je ne sais toujours pas à
              quoi ça sert. Aucune description, rien. Ça me dit cliquer pour
              équiper alors que je doute que ce soit un item à équiper. »
              Le panneau n'affichait que niveau, prix et bonus — un consommable
              n'a aucun des trois à montrer.
            */}
            {vise.objet.effetTexte && <span className="detail-effet">{vise.objet.effetTexte}</span>}
            {/*
              Le VERDICT avant l'ambiance.

              Enzo, sur une pièce de niveau 23 qu'il ne pouvait pas porter au
              19 : « rien n'est écrit sur pourquoi je ne peux pas le porter.
              Ça doit être le niveau mais je ne le vois pas. » C'était écrit,
              et c'était la DERNIÈRE ligne, après 153 caractères de
              description : mesurée à 110 px dans une boîte de 104, donc
              intégralement hors champ.

              L'ordre était le fautif autant que la hauteur. On survole une
              pièce pour savoir ce qu'elle est et si on peut s'en servir ; le
              texte d'ambiance est ce qu'on lit en dernier, quand on a la
              réponse. Il passe donc après, et la réponse remonte sous
              l'identité — là où on la cherche, et là où rien ne peut la
              repousser.
            */}
            {vise.objet.categorie === 'consommable' ? (
              <i className="tenu">{auDoigt ? 'Appuie encore' : 'Clique'} pour la prendre.</i>
            ) : vise.portee ? (
              <i className="tenu">{auDoigt ? 'Appuie encore' : 'Clique'} pour la retirer.</i>
            ) : portable ? (
              <span className="detail-echange">
                <Ecart range={vise.objet} porte={remplacee} />
                <i className="tenu">{auDoigt ? 'Appuie encore' : 'Clique'} pour la porter.</i>
              </span>
            ) : (
              <i className="erreur-tenue">Il te faut le niveau {vise.objet.niveauRequis}.</i>
            )}
            {vise.objet.description && (
              <i className="detail-lore">{vise.objet.description}</i>
            )}
          </>
        ) : (
          <span className="tenu">
            {auDoigt
              ? 'Appuie sur une pièce pour la regarder, ou sur un emplacement vide.'
              : 'Survole une pièce, ou clique un emplacement vide.'}
          </span>
        )}
        </div>
      </div>

      <div className="poupee-sacs">
        <div className="sacs-tete">
          <span>
            {filtre ? `Ce qui va à ${NOMS_EMPLACEMENTS[filtre].toLowerCase()}` : 'Dans les sacs'}
            {` · ${sacs.length}`}
          </span>
          {filtre && (
            <button type="button" className="discret" onClick={() => setFiltre(null)}>
              Tout voir
            </button>
          )}
        </div>

        {sacs.length === 0 ? (
          <p className="tenu sacs-vides">
            {filtre
              ? 'Rien dans tes sacs ne se porte là.'
              : 'Tes sacs sont vides.'}
          </p>
        ) : (
          <div className="grille-sacs">
            {empiler(sacs).map((lot) => (
              <CaseSac
                key={lot.cle}
                objet={lot.tete}
                quantite={lot.quantite}
                portable={lot.tete.niveauRequis <= niveau}
                vise={vise}
                auDoigt={auDoigt}
                surSurvol={setVise}
                // Le filtre dit dans quelle main on veut la pièce. Sans lui, une
                // dague irait toujours à droite, et cliquer « main gauche » puis
                // la dague donnerait le contraire de ce qu'on a demandé.
                surEquiper={(x) => surEquiper(x, filtre ?? undefined)}
                surConsommer={surConsommer}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
